import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { resetPaginationByParamsChanged } from '../../utils/resetPaginationByParamsChanged'
import { AppMultiDropdown } from './AppMultiDropdown'

export interface IAppFilterDropdown<T, TKey> {
  data: T[]
  searchParam: string
  propToShowInList: TKey
  valuePropName: TKey
  placeholder?: string
  label?: string
  id?: string
  shouldResetPagination?: boolean
  disabled?: boolean
  setCurrentValue?: (value: T[]) => void
  border?: boolean
  fullWidth?: boolean
  skipSyncOnInit?: boolean
}

export const AppFilterMultiDropdown = <T, TKey extends keyof T>({
  searchParam,
  data,
  propToShowInList,
  valuePropName,
  label,
  placeholder,
  shouldResetPagination = true,
  setCurrentValue,
  disabled,
  border,
  fullWidth,
  skipSyncOnInit = false,
}: PropsWithChildren<IAppFilterDropdown<T, TKey>>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()
  const [currentFilterValue, setCurrentFilterValue] = useState<T[]>([])
  const currentSearchParamValue = searchParams.get(
    searchParam,
  ) as unknown as TKey[]

  useEffect(() => {
    if (skipSyncOnInit) return
    if (!!currentSearchParamValue) {
      const currentData =
        data &&
        data.filter((item: T) =>
          `${currentSearchParamValue}`
            .split(',')
            .some((i) => i === `${item[valuePropName]}`),
        )
      setCurrentFilterValue(!!currentData ? currentData : [])
      setCurrentValue && setCurrentValue(!!currentData ? currentData : [])
    } else {
      setCurrentFilterValue([])
      setCurrentValue && setCurrentValue([])
    }
  }, [data, currentSearchParamValue])

  const changeHandler = useCallback(
    (item: T[]) => {
      if (!!item.length) {
        searchParams.set(
          searchParam,
          item.map((item) => item[valuePropName]).join(','),
        )
      } else {
        searchParams.delete(searchParam)
      }
      if (shouldResetPagination) {
        resetPaginationByParamsChanged({
          searchParams,
          setSearchParams,
          pathname,
        })
      }
      setCurrentFilterValue(item)
      setSearchParams(searchParams.toString(), { replace: true })
    },
    [searchParams, setSearchParams, searchParam, valuePropName],
  )

  const resetFilterHandler = useCallback(() => {
    searchParams.delete(searchParam)
    setSearchParams(searchParams.toString(), { replace: true })
    if (shouldResetPagination) {
      resetPaginationByParamsChanged({
        searchParams,
        setSearchParams,
        pathname,
      })
    }
  }, [searchParams, searchParam])

  const onDeleteFromList = (valueID: string | number) => {
    if (valuePropName) {
      const removeDuplicate = currentFilterValue.filter(
        (item) => item[valuePropName] !== valueID,
      )
      setCurrentFilterValue(removeDuplicate)
      changeHandler(removeDuplicate)
    }
  }
  return (
    <AppMultiDropdown
      data={data}
      propToShowInList={propToShowInList}
      propToShowInInput={valuePropName}
      value={currentFilterValue}
      placeholder={placeholder}
      onChange={changeHandler}
      label={label}
      resetValueHandler={resetFilterHandler}
      disabled={disabled}
      onDeleteFromList={onDeleteFromList}
      border={border}
      fullWidth={fullWidth}
    />
  )
}
